import createProgram from '../helpers/createProgram';
import createQuadVertexBuffer from '../helpers/createQuadVertexBuffer';

const vectors = ['base', 'mantis'];

const singleLineHeight = 1;

class Brush {
  sizeCoefficient = undefined;

  constructor(gl, shaders, textureManager) {
    this.gl = gl;
    this.shaders = shaders;
    this.textureManager = textureManager;

    this.createBuffers();
    this.createPrograms();

    this.prepareNamesForClearing();
  }

  update(settings) {
    this.settings = settings;

    this.hardReset();

    console.log('Brush settings updated');
  }

  createBuffers() {
    this.simulationFramebuffer = this.gl.createFramebuffer();
    this.quadVertexBuffer = createQuadVertexBuffer(this.gl);
  }

  createPrograms() {
    this.projectProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doubleProjectFrag,
      ['a_position'],
      [
        'u_gravity',
        'u_damping',
        'u_verticesPerBristle',
        'u_resolution',
        'u_positionsTexture_mantis',
        'u_positionsTexture_base',
        'u_velocitiesTexture',
        'u_vectorIndex',
      ]
    );

    this.distanceConstraintProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doubleDistanceConstraintFrag,
      ['a_position'],
      [
        // dont collapse
        'u_pointCount',
        'u_targetDistance',
        'u_pass',
        'u_resolution',
        'u_positionsTexture_mantis',
        'u_positionsTexture_base',
        'u_vectorIndex',
      ]
    );

    this.planeConstraintProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doublePlaneConstraintFrag,
      ['a_position'],
      [
        // empty line
        'u_positionsTexture_mantis',
        'u_positionsTexture_base',
        'u_vectorIndex',
        'u_resolution',
      ]
    );

    this.bendingConstraintProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doubleBendingConstraintFrag,
      ['a_position'],
      [
        'u_pointCount',
        'u_stiffnessVariation',
        'u_pass',
        'u_resolution',
        'u_positionsTexture_mantis',
        'u_positionsTexture_base',
        'u_randomsTexture',
        'u_vectorIndex',
      ]
    );

    this.setBristlesProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doubleSetBristlesFrag,
      ['a_position'],
      [
        'u_brushPosition',
        'u_brushScale',
        'u_bristleCount',
        'u_bristleLength',
        'u_verticesPerBristle',
        'u_jitter',
        'u_resolution',
        'u_randomsTexture',
        'u_vectorIndex',
      ]
    );

    this.updateVelocityProgram = createProgram(
      this.gl,
      this.shaders.fullscreenVert,
      this.shaders.doubleUpdateVelocityFrag,
      ['a_position'],
      ['u_resolution', 'u_positionsTexture_mantis', 'u_positionsTexture_base', 'u_projectedPositionsTexture_mantis', 'u_projectedPositionsTexture_base']
    );
  }

  createBrushTextureCoordinatesBuffer() {
    const brushTextureCoordinates = [];

    for (let bristle = 0; bristle < this.settings.bristleCount; ++bristle) {
      for (let vertex = 0; vertex < this.settings.verticesPerBristle; ++vertex) {
        const textureX = (bristle + 0.5) / this.settings.bristleCount;
        const textureY = (vertex + 0.5) / this.settings.verticesPerBristle;

        brushTextureCoordinates.push(textureX);
        brushTextureCoordinates.push(textureY);
      }
    }

    if (this.brushTextureCoordinatesBuffer) {
      this.gl.deleteBuffer(this.brushTextureCoordinatesBuffer);
    }

    this.brushTextureCoordinatesBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.brushTextureCoordinatesBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(brushTextureCoordinates), this.gl.STATIC_DRAW);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
  }

  createSplatBuffers() {
    const splatsPerSegment = Math.floor(this.settings.splatsPerSegment);

    this.splatsPerSegment = splatsPerSegment;

    this.splatCoordinatesBuffer = [];
    this.splatIndexBuffer = [];
    this.splatIndexCount = [];

    for (let i = 1; i <= splatsPerSegment; i++) {
      this.createSplatBuffersForSizePerSegment(i);
    }
  }

  createSplatBuffersForSizePerSegment(sizePerSegment) {
    const splatCoordinates = [];
    const splatIndices = [];

    let splatIndex = 0;

    const splatsPerSegment = Math.floor(sizePerSegment);

    for (let bristle = 0; bristle < this.settings.bristleCount; ++bristle) {
      for (let vertex = 0; vertex < this.settings.verticesPerBristle - 1; ++vertex) {
        // we create a quad for each bristle vertex

        for (let i = 0; i < splatsPerSegment; ++i) {
          const t = (i + 0.5) / splatsPerSegment;

          const textureX = (bristle + 0.5) / this.settings.bristleCount;
          const textureY = (vertex + 0.5 + t) / this.settings.verticesPerBristle;

          //bottom left
          splatCoordinates.push(textureX);
          splatCoordinates.push(textureY);
          splatCoordinates.push(-1);
          splatCoordinates.push(-1);

          //bottom right
          splatCoordinates.push(textureX);
          splatCoordinates.push(textureY);
          splatCoordinates.push(1);
          splatCoordinates.push(-1);

          //top right
          splatCoordinates.push(textureX);
          splatCoordinates.push(textureY);
          splatCoordinates.push(1);
          splatCoordinates.push(1);

          //top left
          splatCoordinates.push(textureX);
          splatCoordinates.push(textureY);
          splatCoordinates.push(-1);
          splatCoordinates.push(1);

          splatIndices.push(splatIndex + 0);
          splatIndices.push(splatIndex + 1);
          splatIndices.push(splatIndex + 2);

          splatIndices.push(splatIndex + 2);
          splatIndices.push(splatIndex + 3);
          splatIndices.push(splatIndex + 0);

          splatIndex += 4;
        }
      }
    }

    if (this.splatCoordinatesBuffer[splatsPerSegment]) {
      this.gl.deleteBuffer(this.splatCoordinatesBuffer[splatsPerSegment]);
    }

    this.splatCoordinatesBuffer[splatsPerSegment] = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.splatCoordinatesBuffer[splatsPerSegment]);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array(splatCoordinates), this.gl.STATIC_DRAW);

    if (this.splatIndexBuffer[splatsPerSegment]) {
      this.gl.deleteBuffer(this.splatIndexBuffer[splatsPerSegment]);
    }

    this.splatIndexBuffer[splatsPerSegment] = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.splatIndexBuffer[splatsPerSegment]);
    this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(splatIndices), this.gl.STATIC_DRAW);

    this.splatIndexCount[splatsPerSegment] = splatIndices.length;
  }

  createBrushIndexBuffer() {
    const brushIndices = [];
    this.indexCount = 0;

    for (let bristle = 0; bristle < this.settings.bristleCount; ++bristle) {
      for (let vertex = 0; vertex < this.settings.verticesPerBristle - 1; ++vertex) {
        const left = bristle * this.settings.verticesPerBristle + vertex;
        const right = left + 1;

        brushIndices.push(left);
        brushIndices.push(right);

        this.indexCount += 2;
      }
    }

    if (this.brushIndexBuffer) {
      this.gl.deleteBuffer(this.brushIndexBuffer);
    }

    this.brushIndexBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.brushIndexBuffer);
    this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(brushIndices), this.gl.STATIC_DRAW);

    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, null);
  }

  createSpeedArray() {
    this.speeds = []; // most recent speed is stored at the highest index

    for (let i = 0; i < this.settings.nPreviousSpeeds; ++i) {
      this.speeds.push(0);
    }
  }

  getFilteredSpeed() {
    return this.speeds.reduce(function (a, b) {
      return Math.max(a, b);
    });
  }

  getAvgSpeed() {
    return (
      this.speeds.reduce(function (a, b) {
        return a + b;
      }, 0) / (this.speeds.length || 1)
    );
  }

  runProjectProgram(vectorIndex) {
    this.gl.useProgram(this.projectProgram);
    this.gl.viewport(0, 0, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.uniform1f(this.projectProgram.u_gravity, this.settings.brushGravity);
    this.gl.uniform1i(this.projectProgram.u_vectorIndex, vectorIndex);
    this.gl.uniform1f(this.projectProgram.u_damping, this.settings.brushDamping);
    this.gl.uniform2f(this.projectProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    // Activate textures
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushPositionsTexture_mantis);
    this.gl.uniform1i(this.projectProgram.u_positionsTexture_mantis, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushPositionsTexture_base);
    this.gl.uniform1i(this.projectProgram.u_positionsTexture_base, 1);

    this.gl.activeTexture(this.gl.TEXTURE2);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushVelocitiesTexture);
    this.gl.uniform1i(this.projectProgram.u_velocitiesTexture, 2);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.projectProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.projectProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      this.textureManager['brushProjectedPositionsTexture_' + vectors[vectorIndex]],
      0
    );

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  runProjectProgramPackage() {
    for (let i = 0; i < vectors.length; i++) {
      this.runProjectProgram(i);
    }
  }

  runSetBristlesProgram(width, height, texture, vectorIndex) {
    this.gl.useProgram(this.setBristlesProgram);
    this.gl.viewport(0, 0, width, height);

    const brushScale = this.settings.brushScale * this.sizeCoefficient;

    this.gl.uniform3f(this.setBristlesProgram.u_brushPosition, this.positionX, this.positionY, this.positionZ);
    this.gl.uniform1f(this.setBristlesProgram.u_brushScale, brushScale);
    this.gl.uniform1i(this.setBristlesProgram.u_vectorIndex, vectorIndex);
    this.gl.uniform1f(this.setBristlesProgram.u_bristleCount, this.settings.bristleCount);
    this.gl.uniform1f(this.setBristlesProgram.u_bristleLength, this.settings.bristleLength);
    this.gl.uniform1f(this.setBristlesProgram.u_jitter, this.settings.bristleJitter);
    this.gl.uniform1f(this.setBristlesProgram.u_verticesPerBristle, this.settings.verticesPerBristle);
    this.gl.uniform2f(this.setBristlesProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    // Activate texture
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushRandomsTexture);
    this.gl.uniform1i(this.setBristlesProgram.u_randomsTexture, 0);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.setBristlesProgram.a_position, 2, this.gl.FLOAT, false, 0, 0); // feed it
    this.gl.enableVertexAttribArray(this.setBristlesProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, texture, 0);

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  runDistanceConstraintProgramPackage(pass) {
    for (let i = 0; i < vectors.length; i++) {
      this.runDistanceConstraintProgram(pass, i);
    }
  }

  runDistanceConstraintProgram(pass, vectorIndex) {
    this.gl.useProgram(this.distanceConstraintProgram);
    this.gl.viewport(0, 0, this.settings.bristleCount, this.settings.verticesPerBristle);

    const brushScale = this.settings.brushScale * this.sizeCoefficient;
    const targetDistance = (brushScale * this.settings.bristleLength) / (this.settings.verticesPerBristle - 1);

    this.gl.uniform1f(this.distanceConstraintProgram.u_pointCount, this.settings.verticesPerBristle);
    this.gl.uniform1f(this.distanceConstraintProgram.u_targetDistance, targetDistance);
    this.gl.uniform1i(this.distanceConstraintProgram.u_pass, pass);
    this.gl.uniform1i(this.distanceConstraintProgram.u_vectorIndex, vectorIndex);
    this.gl.uniform2f(this.distanceConstraintProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_mantis);
    this.gl.uniform1i(this.distanceConstraintProgram.u_positionsTexture_mantis, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_base);
    this.gl.uniform1i(this.distanceConstraintProgram.u_positionsTexture_base, 1);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.distanceConstraintProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.distanceConstraintProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      this.textureManager['brushProjectedPositionsTempTexture_' + vectors[vectorIndex]],
      0
    );

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  runBendingConstraintProgramPackage(pass) {
    for (let i = 0; i < vectors.length; i++) {
      this.runBendingConstraintProgram(pass, i);
    }
  }

  runBendingConstraintProgram(pass, vectorIndex) {
    this.gl.useProgram(this.bendingConstraintProgram);
    this.gl.viewport(0, 0, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.uniform1f(this.bendingConstraintProgram.u_pointCount, this.settings.verticesPerBristle);
    this.gl.uniform1f(this.bendingConstraintProgram.u_stiffnessVariation, this.settings.stiffnessVariation);
    this.gl.uniform1i(this.bendingConstraintProgram.u_pass, pass);
    this.gl.uniform1i(this.bendingConstraintProgram.u_vectorIndex, vectorIndex);
    this.gl.uniform2f(this.bendingConstraintProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_mantis);
    this.gl.uniform1i(this.bendingConstraintProgram.u_positionsTexture_mantis, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_base);
    this.gl.uniform1i(this.bendingConstraintProgram.u_positionsTexture_base, 1);

    this.gl.activeTexture(this.gl.TEXTURE2);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushRandomsTexture);
    this.gl.uniform1i(this.bendingConstraintProgram.u_randomsTexture, 2);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.bendingConstraintProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.bendingConstraintProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      this.textureManager['brushProjectedPositionsTempTexture_' + vectors[vectorIndex]],
      0
    );

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  runPlaneConstraintProgramPackage() {
    for (let i = 0; i < vectors.length; i++) {
      this.runPlaneConstraintProgram(i);
    }
  }

  runPlaneConstraintProgram(vectorIndex) {
    this.gl.useProgram(this.planeConstraintProgram);
    this.gl.viewport(0, 0, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.uniform2f(this.planeConstraintProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.uniform1i(this.planeConstraintProgram.u_vectorIndex, vectorIndex);

    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_mantis);
    this.gl.uniform1i(this.planeConstraintProgram.u_positionsTexture_mantis, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_base);
    this.gl.uniform1i(this.planeConstraintProgram.u_positionsTexture_base, 1);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.planeConstraintProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.planeConstraintProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(
      this.gl.FRAMEBUFFER,
      this.gl.COLOR_ATTACHMENT0,
      this.gl.TEXTURE_2D,
      this.textureManager['brushProjectedPositionsTempTexture_' + vectors[vectorIndex]],
      0
    );

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  runUpdateVelocityProgram() {
    this.gl.useProgram(this.updateVelocityProgram);
    this.gl.viewport(0, 0, this.settings.bristleCount, this.settings.verticesPerBristle);

    this.gl.uniform2f(this.updateVelocityProgram.u_resolution, this.settings.bristleCount, this.settings.verticesPerBristle);

    // Activate textures
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushPositionsTexture_mantis);
    this.gl.uniform1i(this.updateVelocityProgram.u_positionsTexture_mantis, 0);

    this.gl.activeTexture(this.gl.TEXTURE1);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushPositionsTexture_base);
    this.gl.uniform1i(this.updateVelocityProgram.u_positionsTexture_base, 1);

    this.gl.activeTexture(this.gl.TEXTURE2);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_mantis);
    this.gl.uniform1i(this.updateVelocityProgram.u_projectedPositionsTexture_mantis, 2);

    this.gl.activeTexture(this.gl.TEXTURE3);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.textureManager.brushProjectedPositionsTexture_base);
    this.gl.uniform1i(this.updateVelocityProgram.u_projectedPositionsTexture_base, 3);

    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.quadVertexBuffer);
    this.gl.vertexAttribPointer(this.updateVelocityProgram.a_position, 2, this.gl.FLOAT, false, 0, 0);
    this.gl.enableVertexAttribArray(this.updateVelocityProgram.a_position);

    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.simulationFramebuffer);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.textureManager.brushPreviousVelocitiesTexture, 0);

    this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
  }

  getPenForceSizeIsOn() {
    return this.settings.primaryPenForceBrushSizeIsOn;
  }

  initialize(x, y) {
    if (!this.settings) {
      console.log('no settings in Brush');

      this.sizeCoefficient = 0.0;
      this.positionZ = 0.0;
    } else {
      const penForceSizeIsOn = this.getPenForceSizeIsOn();

      this.sizeCoefficient = penForceSizeIsOn ? 0.0 : 1.0;

      const brushScale = this.settings.brushScale * this.sizeCoefficient;

      this.positionZ = this.settings.brushHeight * brushScale;
    }

    this.positionX = x;
    this.positionY = y;

    this.reset();
  }

  swapPackage(a, b) {
    for (let i = 0; i < vectors.length; i++) {
      this.textureManager.swap(a + '_' + vectors[i], b + '_' + vectors[i]);
    }
  }

  hardReset() {
    console.log('hard reset');

    this.textureManager.createBrushTextures(this.settings.bristleCount, this.settings.verticesPerBristle);
    this.textureManager.createRandomTexture(this.settings.bristleCount, this.settings.verticesPerBristle, 'brushRandomsTexture');

    this.createBrushTextureCoordinatesBuffer();
    this.createSplatBuffers();
    this.createBrushIndexBuffer();

    this.reset();
  }

  prepareNamesForClearing() {
    const xyzNames = ['brushPositionsTexture', 'brushPreviousPositionsTexture', 'brushProjectedPositionsTexture', 'brushProjectedPositionsTempTexture'];

    const velocityNames = ['brushVelocitiesTexture', 'brushPreviousVelocitiesTexture'];

    const names = [];

    for (let i = 0; i < xyzNames.length; i++) {
      for (let j = 0; j < vectors.length; j++) {
        names.push(xyzNames[i] + '_' + vectors[j]);
      }
    }

    names.push(...velocityNames);

    this.namesToClear = names;
  }

  reset() {
    console.log('soft reset');

    this.textureManager.clearByNames(this.namesToClear);

    this.createSpeedArray();

    this.runSetBristlesProgramPackage('brushPositionsTexture', this.settings.verticesPerBristle);
  }

  runSetBristlesProgramPackage(textureName, height) {
    for (let i = 0; i < vectors.length; i++) {
      this.runSetBristlesProgram(this.settings.bristleCount, height, this.textureManager[textureName + '_' + vectors[i]], i);
    }
  }

  setPosition(x, y, pressure) {
    this.gl.disable(this.gl.DEPTH_TEST);
    this.gl.disable(this.gl.BLEND);
    this.gl.disable(this.gl.SCISSOR_TEST);

    const dx = x - this.positionX;
    const dy = y - this.positionY;

    const speed = Math.hypot(dx, dy);

    this.speeds.shift();
    this.speeds.push(speed);

    this.sizeCoefficient = this.settings.primary.penForceBrushSizeFunction(pressure);

    const brushScale = this.settings.brushScale * this.sizeCoefficient;

    this.positionX = x;
    this.positionY = y;
    this.positionZ = this.settings.brushHeight * brushScale;

    this.runProjectProgramPackage();

    this.runSetBristlesProgramPackage('brushProjectedPositionsTexture', singleLineHeight);

    for (let i = 0; i < this.settings.brushIterations; ++i) {
      this.runSetBristlesProgramPackage('brushProjectedPositionsTexture', singleLineHeight);

      for (let pass = 0; pass < 2; ++pass) {
        this.runDistanceConstraintProgramPackage(pass);

        this.swapPackage('brushProjectedPositionsTexture', 'brushProjectedPositionsTempTexture');
      }

      for (let pass = 0; pass < 3; ++pass) {
        this.runBendingConstraintProgramPackage(pass);

        this.swapPackage('brushProjectedPositionsTexture', 'brushProjectedPositionsTempTexture');
      }

      this.runPlaneConstraintProgramPackage();

      this.swapPackage('brushProjectedPositionsTexture', 'brushProjectedPositionsTempTexture');
    }

    this.runUpdateVelocityProgram();

    this.textureManager.swap('brushVelocitiesTexture', 'brushPreviousVelocitiesTexture');

    this.swapPackage('brushPreviousPositionsTexture', 'brushPositionsTexture');
    this.swapPackage('brushPositionsTexture', 'brushProjectedPositionsTexture');
  }
}

export default Brush;
